import React, { useEffect, useRef, useState } from 'react';
import axios from '../../../api/axios';
import { toast } from 'react-toastify';
import { getSiteAdminTemplatesRoute, updateTemplateFlagsRoute } from '../../../api/routes';
import { ReactComponent as PencilIcon } from '../../../assets/icons/Pencil.svg';
import config from '../../../config';
import LottieIcon from '../../../components/common/LottieIcon';
import '../SiteAdmin.scss';

const toDisplayUrl = (url) =>
    url && url.startsWith('/files/') ? `${config.apiBaseUrl}${url}` : (url || '');

// Group templates by featureType
const groupByFeatureType = (templates) => {
    const groups = {};
    templates.forEach(t => {
        const key = t.featureType || 'Uncategorised';
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
};

// ── Toggle component ───────────────────────────────────────────────────────────

const FlagToggle = ({ label, value, onChange, saving }) => (
    <div className="tm-flag" onClick={() => !saving && onChange(!value)}>
        <div className={`tm-flag-toggle${value ? ' on' : ''}`} />
        <span>{label}</span>
    </div>
);

// ── Template row ───────────────────────────────────────────────────────────────

const TemplateRow = ({ template, onFlagChange, onEdit }) => {
    const [saving, setSaving] = useState(false);
    const [descOverflows, setDescOverflows] = useState(false);
    const descTextRef = useRef(null);

    const handleFlag = async (flag, value) => {
        setSaving(true);
        try {
            const res = await axios.put(
                `${updateTemplateFlagsRoute}/${template.id}/flags`,
                { [flag]: value },
                { withCredentials: true }
            );
            if (res.data.success) {
                onFlagChange(template.id, { [flag]: value });
            }
        } catch {
            toast.error('Failed to update template');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="template-manager-row">
            <div
                className="tm-thumb"
                style={template.templateImage ? { backgroundImage: `url(${toDisplayUrl(template.templateImage)})` } : {}}
            />
            <div className="tm-info">
                <div className="tm-name">{template.name}</div>
                <div className="tm-type">
                    {template.featureType}{template.templateType === 'campaign' ? ' · Campaign' : ''}
                    {' '}
                    <span className={`tcm-status-badge${template.isActive === true ? ' active' : ' inactive'}`}>
                        {template.isActive === true ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </div>
            <div className="tm-desc" onMouseEnter={() => {
                if (descTextRef.current) setDescOverflows(descTextRef.current.scrollHeight > descTextRef.current.clientHeight);
            }}>
                <span className="tm-desc-text" ref={descTextRef}>{template.description || ''}</span>
                {template.description && descOverflows && (
                    <div className="tm-desc-tooltip">{template.description}</div>
                )}
            </div>
            <div className="tm-feature-icons">
                {(template.features || []).map((f, i) => (
                    f.featureType === 'feature' ? (
                        <div key={i} className={`tm-fi-main category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={26} />
                        </div>
                    ) : (
                        <div key={i} className={`tm-fi-disruptor category-icon-bg--${((Array.isArray(f.featureCategory) ? f.featureCategory[0] : f.featureCategory) || 'misc').toLowerCase()}`} title={f.contentLabel || f.featureName || f.varKey}>
                            <LottieIcon varKey={f.varKey} size={13} />
                        </div>
                    )
                ))}
            </div>
            <div className="tm-flags">
                <FlagToggle
                    label="Quickstart"
                    value={!!template.isQuickstart}
                    saving={saving}
                    onChange={v => handleFlag('isQuickstart', v)}
                />
                <FlagToggle
                    label="Featured"
                    value={!!template.isFeatured}
                    saving={saving}
                    onChange={v => handleFlag('isFeatured', v)}
                />
            </div>
            <div className="tm-edit-icon" onClick={() => onEdit(template)} title="Edit template">
                <PencilIcon className="tm-pencil" aria-label="Edit" />
            </div>
        </div>
    );
};

// ── Main TemplateManager ───────────────────────────────────────────────────────

const TemplateManager = ({ onEdit, onCreate, templatePatch, onTemplatesLoaded, onFlagChange: onFlagChangeExternal }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [filterQuickstart, setFilterQuickstart] = useState(false);
    const [filterFeatured, setFilterFeatured] = useState(false);

    useEffect(() => {
        if (templatePatch) {
            setTemplates(prev => prev.map(t => t.id === templatePatch.id ? { ...t, ...templatePatch } : t));
        }
    }, [templatePatch]);

    useEffect(() => {
        axios.get(getSiteAdminTemplatesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) {
                    setTemplates(res.data.templates);
                    if (onTemplatesLoaded) onTemplatesLoaded(res.data.templates);
                }
            })
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFlagChange = (id, patch) => {
        setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
        if (onFlagChangeExternal) onFlagChangeExternal(id, patch);
    };

    let filtered = search
        ? templates.filter(t =>
            t.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.featureType?.toLowerCase().includes(search.toLowerCase())
          )
        : templates;
    if (filterActive === 'active') filtered = filtered.filter(t => t.isActive === true);
    if (filterActive === 'inactive') filtered = filtered.filter(t => t.isActive !== true);
    if (filterQuickstart) filtered = filtered.filter(t => t.isQuickstart);
    if (filterFeatured) filtered = filtered.filter(t => t.isFeatured);

    const groups = groupByFeatureType(filtered);

    if (loading) return <div className="home-loading-placeholder" />;

    return (
        <div>
            {onCreate && (
                <div style={{ marginBottom: '1rem', width: "150px" }}>
                    <div className="standard-button button-active" onClick={onCreate}>+ Create Template</div>
                </div>
            )}
            <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <input
                    className="sa-text-input"
                    style={{ maxWidth: '320px' }}
                    placeholder="Search templates…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <select
                    className="sa-select sa-select--narrow"
                    value={filterActive}
                    onChange={e => setFilterActive(e.target.value)}
                >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
                <FlagToggle
                    label="Quickstart"
                    value={filterQuickstart}
                    saving={false}
                    onChange={setFilterQuickstart}
                />
                <FlagToggle
                    label="Featured"
                    value={filterFeatured}
                    saving={false}
                    onChange={setFilterFeatured}
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--label-color)' }}>
                    {filtered.length} template{filtered.length !== 1 ? 's' : ''}
                    {' · '}
                    {filtered.filter(t => t.isQuickstart).length} quickstart
                    {' · '}
                    {filtered.filter(t => t.isFeatured).length} featured
                </span>
            </div>

            <div className="template-manager-grid">
                {groups.map(([groupName, groupTemplates]) => (
                    <div key={groupName}>
                        <div className="template-manager-group-title">{groupName}</div>
                        {groupTemplates.map(t => (
                            <TemplateRow key={t.id} template={t} onFlagChange={handleFlagChange} onEdit={onEdit} />
                        ))}
                    </div>
                ))}

                {groups.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--label-color)' }}>
                        No templates found.
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateManager;
